/**
 * Blog Posts Index
 *
 * This file imports and exports all blog posts and articles.
 * To add a new post:
 * 1. Create a new file in blogs/ or articles/ folder
 * 2. Import it below
 * 3. Add it to the allPosts array
 */

// Import blog posts
import exoHavenConcept from "./blogs/exo-haven-concept";
import exoHavenCourse from "./blogs/exo-haven-course";
import exoHavenWhy from "./blogs/exo-haven-why";
import exoHavenDeliberatePractice from "./blogs/exo-haven-deliberate-practice";
import sandboxedAgentsGuide from "./blogs/sandboxed-agents-guide";
import jasperRecallGuide from "./blogs/jasper-recall-guide";
import jasperRecallSharedMemory from "./blogs/jasper-recall-shared-memory";
import exoDistillerSelfImproving from "./blogs/exo-distiller-self-improving-agents";
import jasperForgeBehavioralLinting from "./blogs/jasper-forge-behavioral-linting";
import founderLogDay117 from "./blogs/founder-log-day-117";

// Import articles
import exoHavenResilience from "./articles/exo-haven-resilience";
import businessSystems from "./articles/business-systems";
import studyThePros from "./articles/study-the-pros";
import battingPractice from "./articles/batting-practice";
import indextts2MacInstallGuide from "./articles/indextts2-mac-install-guide";

// Combine all posts
export const allPosts = [
  // Blog posts
  exoHavenConcept,
  exoHavenCourse,
  exoHavenWhy,
  exoHavenDeliberatePractice,
  sandboxedAgentsGuide,
  jasperRecallGuide,
  jasperRecallSharedMemory,
  exoDistillerSelfImproving,
  jasperForgeBehavioralLinting,
  founderLogDay117,

  // Articles
  exoHavenResilience,
  businessSystems,
  studyThePros,
  battingPractice,
  indextts2MacInstallGuide,
];

// Helper functions
export const getPostBySlug = slug => {
  return allPosts.find(post => post.slug === slug);
};

export const getPublishedPosts = () => {
  return allPosts
    .filter(post => post.status !== "draft") // Filter out draft posts
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getPostsByType = contentType => {
  return allPosts
    .filter(post => post.contentType === contentType && post.status !== "draft")
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getPostsByCategory = category => {
  return allPosts
    .filter(post => post.categories.includes(category) && post.status !== "draft")
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getPostsByTag = tag => {
  return allPosts
    .filter(post => post.tags.includes(tag) && post.status !== "draft")
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getDraftPosts = () => {
  return allPosts
    .filter(post => post.status === "draft")
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getAllPostsIncludingDrafts = () => {
  return allPosts.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getTemplatePosts = () => {
  return allPosts
    .filter(post => post.isTemplate === true)
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
};

export const getAllCategories = () => {
  const categories = new Set();
  allPosts.forEach(post => {
    if (post.status !== "draft") {
      post.categories.forEach(cat => categories.add(cat));
    }
  });
  return Array.from(categories).sort();
};

export const getAllTags = () => {
  const tags = new Set();
  allPosts.forEach(post => {
    if (post.status !== "draft") {
      post.tags.forEach(tag => tags.add(tag));
    }
  });
  return Array.from(tags).sort();
};

export default allPosts;
